import React, { useContext, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Card, CardContent, MenuItem, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import ListItemText from '@mui/material/ListItemText';
import { Link as RouterLink } from 'react-router-dom';
import DropDownMenu from '@/components/DropDownMenu/DropDownMenu';
import Typography from '@mui/material/Typography';
import { NewsStatusType } from '@/api';
import moment from 'moment';

const PREFIX = 'MailingTable';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  inner: `${PREFIX}-inner`,
  nameContainer: `${PREFIX}-nameContainer`,
  avatar: `${PREFIX}-avatar`,
  actions: `${PREFIX}-actions`,
  fab: `${PREFIX}-fab`,
  hint: `${PREFIX}-hint`,
  photo: `${PREFIX}-photo`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflowX: 'auto',
  },

  [`& .${classes.content}`]: {
    padding: 0,
  },

  [`& .${classes.inner}`]: {
    minWidth: 600,
  },

  [`& .${classes.nameContainer}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.avatar}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    justifyContent: 'flex-end',
  },

  [`& .${classes.fab}`]: {
    margin: theme.spacing(1),
  },

  [`& .${classes.hint}`]: {
    marginLeft: 10,
    height: 16,
    width: 16,
    minHeight: 16,
  },

  [`& .${classes.photo}`]: {
    maxWidth: '70px',
    maxHeight: '70px',
    borderRadius: '7%',
  },
}));

const shortenTime = time => {
  return moment(time).locale('ru').format('DD MMMM HH:mm');
};

const sendingTime = n => {
  if (n.status === NewsStatusType.Draft || n.status === NewsStatusType.ReadyToSend) {
    if (n.sendingTime.immediate) {
      return 'Сразу';
    } else {
      return shortenTime(n.sendingTime.time);
    }
  }
  if (n.status === NewsStatusType.Sending) {
    return shortenTime(n.progress.startTime);
  }
  if (n.status === NewsStatusType.Published) {
    return shortenTime(n.progress.endTime);
  }
  return 'fallback';
};

const sendingStatus = n => {
  switch (n.status) {
    case NewsStatusType.Draft:
      return 'Черновик';
    case NewsStatusType.ReadyToSend:
      return 'Назначено к отправке';
    case NewsStatusType.Sending:
      if (n.progress.notSent === 0)
        return `Отправляется (${n.progress.completed}/${n.progress.scheduled + n.progress.completed})`;
      else
        return `Отправляется ${n.progress.completed}/${n.progress.scheduled + n.progress.completed}, с ошибкой: ${
          n.progress.notSent
        }`;
    case NewsStatusType.Published:
      if (n.progress.notSent === 0) return `Отправлено (${n.progress.completed})`;
      else return `Отправлено ${n.progress.completed}, с ошибкой: ${n.progress.notSent}`;
  }
};

const MailingTable = props => {
  const { className, meetupId, mailings, handleDelete, ...rest } = props;

  if (!mailings) {
    return null;
  }

  const textForRecipients = it => {
    let ret = '';
    const rc = it.recipients;
    if (rc) {
      if (rc.registered !== null && rc.registered.length > 0) {
        ret += 'Регистрация: ' + rc.registered.join(', ');
      }
      if (rc.groups !== null && rc.groups.length > 0) {
        ret += 'Группа: ' + rc.groups.join(', ');
      }
      if (rc.statuses !== null && rc.statuses.length > 0) {
        if (ret.length > 0) {
          ret += '\n';
        }
        ret += 'Статус: ' + rc.statuses.join(', ');
      }
      if (rc.tags !== null && rc.tags.length > 0) {
        if (ret.length > 0) {
          ret += '\n';
        }
        ret += 'Тэги: ' + rc.tags.join(', ');
      }
    }
    let count = rc.preliminaryCount;
    if (it.status !== NewsStatusType.Draft) {
      count = it.progress.scheduled + it.progress.completed;
    }

    if (ret.length === 0) {
      return `Все (${count})`;
    } else {
      ret += '\n';
      ret += `(${count})`;
    }

    return ret;
  };

  return (
    <StyledCard {...rest} className={clsx(classes.root, className)}>
      <CardContent className={classes.content}>
        <div className={classes.inner}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Время отправки</TableCell>
                <TableCell>Текст</TableCell>
                <TableCell>Получатели</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mailings.map((it, i) => (
                <TableRow className={classes.tableRow} hover key={it.mailingId}>
                  <TableCell style={{ width: '200px' }}>
                    <ListItemText primary={sendingTime(it)} secondary={sendingStatus(it)} />
                  </TableCell>
                  <TableCell>
                    <RouterLink to={'/m/' + meetupId + '/mailing/id/' + it.mailingId}>
                      <Typography style={{ fontWeight: 'bold' }}>
                        {(it.title || it.text || 'Без текста').replace(/(<([^>]+)>)/gi, '')}
                      </Typography>
                      {it.title && it.text && (
                        <Typography variant={'subtitle2'}>{it.text.replace(/(<([^>]+)>)/gi, '')}</Typography>
                      )}
                    </RouterLink>
                  </TableCell>
                  <TableCell style={{ width: '200px' }}>
                    <Typography style={{ whiteSpace: 'pre-wrap' }}>{textForRecipients(it)}</Typography>
                  </TableCell>

                  <TableCell style={{ textAlign: 'center', width: '100px' }}>
                    <DropDownMenu data-test-id='merch-menu'>
                      <MenuItem>
                        <RouterLink
                          to={'/m/' + meetupId + '/mailing/id/' + it.mailingId}
                          data-test-id='mailing-menu-edit'
                        >
                          <ListItemText primary='Редактировать' />
                        </RouterLink>
                      </MenuItem>
                      <MenuItem onClick={() => handleDelete(meetupId, it.mailingId)} data-test-id='mailing-menu-delete'>
                        <ListItemText primary='Удалить' />
                      </MenuItem>
                    </DropDownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </StyledCard>
  );
};

MailingTable.propTypes = {
  className: PropTypes.string,
  mailings: PropTypes.array.isRequired,
  handleDelete: PropTypes.func,
};

export default MailingTable;
